// ============================================================================
// Bases y Propiedades — booking confirmation flow
// ============================================================================
// When a reservation is confirmed (by staff or from the public page) this
// orchestrates the same downstream actions the issue asks for, mirroring what
// happens when you buy a seat or check out in POS/Ecommerce:
//   1. Purchase/Invoice  → allocate a FAC-YYYY-##### number + downloadable PDF.
//   2. Google Calendar    → build an "add to calendar" link + an .ics file with
//                           an invitation for the customer.
//   3. Email              → build a mailto: with the confirmation summary.
//
// The PDF is generated client-side with jsPDF (already a dependency, same lib
// the rest of the app uses for documents). The calendar/email steps are real,
// shareable artifacts; the heavyweight server side (Stripe charge, pdfkit on
// Cloud Functions, Google OAuth event creation, SMTP/Resend send) reuses the
// existing services and is wired when the backend endpoints are connected.
// ============================================================================

import jsPDF from "jspdf";
import i18n from "../../../i18n";
import type { Booking, Amenity } from "../store";

// Translate a `common` namespace key. These artifacts (PDF / .ics / email) are
// generated outside React, so we read from the shared i18n instance directly;
// it reflects the user's active language just like the rest of the app.
const ti = (key: string, opts?: Record<string, unknown>): string =>
  i18n.t(key, { ns: "common", ...opts }) as string;

const channelLabel = (channel: Booking["channel"]): string =>
  channel === "staff"
    ? ti("basesPropiedades.invoice.staffChannel")
    : ti("basesPropiedades.invoice.publicChannel");

// ---------------------------------------------------------------------------
// Invoice PDF (downloadable) — FAC-YYYY-#####
// ---------------------------------------------------------------------------
export function generateInvoicePdf(
  booking: Booking,
  amenity: Amenity | undefined,
  companyName: string,
): void {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const left = 48;
  let y = 64;

  doc.setFontSize(20);
  doc.text(ti("basesPropiedades.invoice.docTitle"), left, y);
  y += 28;

  doc.setFontSize(11);
  doc.text(companyName || ti("basesPropiedades.invoice.defaultCompany"), left, y);
  y += 18;
  doc.text(`${ti("basesPropiedades.invoice.folio")}: ${booking.invoiceNumber ?? "—"}`, left, y);
  y += 16;
  doc.text(
    `${ti("basesPropiedades.invoice.issueDate")}: ${new Date().toLocaleDateString()}`,
    left,
    y,
  );
  y += 28;

  doc.setFontSize(13);
  doc.text(ti("basesPropiedades.invoice.detailTitle"), left, y);
  y += 20;

  doc.setFontSize(11);
  const amenityType = amenity
    ? ti(`basesPropiedades.amenityTypes.${amenity.type}`, { defaultValue: amenity.type })
    : "—";
  const paymentStatus = ti(
    `basesPropiedades.paymentStatus.${booking.paymentStatus}`,
    { defaultValue: booking.paymentStatus },
  );
  const lines = [
    `${ti("basesPropiedades.invoice.amenity")}: ${booking.amenityName}`,
    `${ti("basesPropiedades.invoice.type")}: ${amenityType}`,
    `${ti("basesPropiedades.invoice.customer")}: ${booking.customerName}`,
    `${ti("basesPropiedades.invoice.email")}: ${booking.customerEmail || "—"}`,
    `${ti("basesPropiedades.invoice.date")}: ${booking.date}`,
    `${ti("basesPropiedades.invoice.schedule")}: ${booking.startTime} – ${booking.endTime}`,
    `${ti("basesPropiedades.invoice.channel")}: ${channelLabel(booking.channel)}`,
    `${ti("basesPropiedades.invoice.paymentStatus")}: ${paymentStatus}`,
  ];
  for (const line of lines) {
    doc.text(line, left, y);
    y += 16;
  }

  y += 16;
  doc.setFontSize(14);
  const totalLabel = ti("basesPropiedades.invoice.total");
  const total =
    booking.price > 0
      ? `${totalLabel}: $${booking.price.toFixed(2)} ${amenity?.currency ?? "MXN"}`
      : `${totalLabel}: ${ti("basesPropiedades.invoice.free")}`;
  doc.text(total, left, y);

  y += 40;
  doc.setFontSize(9);
  doc.setTextColor(120);
  doc.text(ti("basesPropiedades.invoice.footer"), left, y);

  doc.save(`${booking.invoiceNumber ?? "factura"}.pdf`);
}

// ---------------------------------------------------------------------------
// Google Calendar + invitation (.ics)
// ---------------------------------------------------------------------------

/** Convert a YYYY-MM-DD + HH:mm into the compact UTC-less calendar stamp. */
function toCalStamp(date: string, time: string): string {
  return `${date.replace(/-/g, "")}T${time.replace(":", "")}00`;
}

/** "Add to Google Calendar" URL with the customer pre-filled as a guest. */
export function buildGoogleCalendarUrl(booking: Booking): string {
  const dates = `${toCalStamp(booking.date, booking.startTime)}/${toCalStamp(
    booking.date,
    booking.endTime,
  )}`;
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: ti("basesPropiedades.calendar.eventTitle", { name: booking.amenityName }),
    dates,
    details: ti("basesPropiedades.calendar.eventDetails", {
      name: booking.amenityName,
      customer: booking.customerName,
      invoice: booking.invoiceNumber ?? "—",
    }),
    add: booking.customerEmail || "",
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

/** Build a downloadable .ics calendar invitation for the booking. */
export function downloadIcsInvite(booking: Booking): void {
  const ics = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Nuvex//Bases y Propiedades//ES",
    "CALSCALE:GREGORIAN",
    "METHOD:REQUEST",
    "BEGIN:VEVENT",
    `UID:${booking.id}@bases-propiedades`,
    `SUMMARY:${ti("basesPropiedades.calendar.eventTitle", { name: booking.amenityName })}`,
    `DTSTART:${toCalStamp(booking.date, booking.startTime)}`,
    `DTEND:${toCalStamp(booking.date, booking.endTime)}`,
    `DESCRIPTION:${ti("basesPropiedades.calendar.icsDescription", {
      customer: booking.customerName,
      invoice: booking.invoiceNumber ?? "—",
    })}`,
    booking.customerEmail
      ? `ATTENDEE;CN=${booking.customerName};RSVP=TRUE:mailto:${booking.customerEmail}`
      : "",
    "STATUS:CONFIRMED",
    "END:VEVENT",
    "END:VCALENDAR",
  ]
    .filter(Boolean)
    .join("\r\n");

  const blob = new Blob([ics], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `reserva-${booking.id}.ics`;
  link.click();
  URL.revokeObjectURL(url);
}

// ---------------------------------------------------------------------------
// Email confirmation (mailto)
// ---------------------------------------------------------------------------
export function buildConfirmationMailto(booking: Booking): string {
  const subject = ti("basesPropiedades.email.subject", { name: booking.amenityName });
  const body = [
    ti("basesPropiedades.email.greeting", { customer: booking.customerName }),
    "",
    ti("basesPropiedades.email.confirmedLine", { name: booking.amenityName }),
    ti("basesPropiedades.email.dateLine", { date: booking.date }),
    ti("basesPropiedades.email.scheduleLine", {
      start: booking.startTime,
      end: booking.endTime,
    }),
    booking.price > 0
      ? ti("basesPropiedades.email.totalLine", { price: booking.price.toFixed(2) })
      : ti("basesPropiedades.email.freeLine"),
    booking.invoiceNumber
      ? ti("basesPropiedades.email.folioLine", { invoice: booking.invoiceNumber })
      : "",
    "",
    ti("basesPropiedades.email.closing"),
  ]
    .filter(Boolean)
    .join("\n");
  return `mailto:${booking.customerEmail}?subject=${encodeURIComponent(
    subject,
  )}&body=${encodeURIComponent(body)}`;
}
